import {createSlice} from "@reduxjs/toolkit";
import {User} from "../graphql/types";
import {RequestPageType} from "../types/RequestPageType";
import {PageNode} from "../types/PageNode";
import { ASSENTS_LINKS } from "../components/context-menu/modals/CssLinkModal";

type TPublishStatus = {
    isLoading: boolean
    isCompleted: boolean
}

const storedLinks: string[] = JSON.parse(localStorage.getItem(ASSENTS_LINKS))
interface AppState {
    isMobileDisplayMode: boolean,
    isMobileViewDimension: boolean,
    user: User,
    isExportState: boolean,
    isPublishPageState: boolean,
    publishEventContext: { source_block?: string; method?: string },
    showHiddenBlocks: boolean,
    preview: boolean
    links: any[],
    title: string,
    type: RequestPageType|null,
    pageId: any
    isAdvancedMode: boolean
    isReadOnlyMode: boolean
    screenShotNodeId: { id: string; key: number } | undefined,
    newNode: PageNode,
    initPublishPage: boolean
    publishStatus: TPublishStatus
    isCopiedContent: boolean
    isClearElements: boolean
    isRemovedBlock: boolean
    isShowPreloader: boolean
    isInitApp: boolean
    premiumMeQr: boolean
    isCopiedBlock: boolean
    isNotShowPreviewToast: boolean
    subPageLoading: boolean
    isLoaded: boolean
    iframeError: boolean,
    isIframeOnMeQr: boolean,
    formPreview: boolean,
    nodesIdsWithWrongAction: string[],
    pageWatermark: boolean,
    isAccountBlocked: boolean,
}

const initialState: AppState = {
    isMobileDisplayMode: false,
    isMobileViewDimension: false,
    user: null,
    isExportState: false,
    isPublishPageState: false,
    publishEventContext: {},
    showHiddenBlocks: false,
    preview: false,
    links: storedLinks || [],
    title: 'No name',
    type: null,
    pageId: undefined,
    isAdvancedMode: false,
    isReadOnlyMode: true,
    screenShotNodeId: undefined,
    newNode: null,
    initPublishPage: false,
    publishStatus: {
        isLoading: false,
        isCompleted: false
    },
    isCopiedContent: false,
    isClearElements: false,
    isRemovedBlock: false,
    isShowPreloader: false,
    isInitApp: false,
    premiumMeQr: false,
    isCopiedBlock: false,
    isNotShowPreviewToast: false,
    subPageLoading: false,
    isLoaded: false,
    iframeError: false,
    isIframeOnMeQr: false,
    formPreview: false,
    nodesIdsWithWrongAction: [],
    pageWatermark: true,
    isAccountBlocked: false,
}
export const AppStorage = createSlice({
    name: "app",
    initialState,
    reducers: {
        changePreview: (state, action) => {
            state.preview = action.payload;
        },
        addLink: (state, action) => {
            state.links.push(action.payload);
        },
        removeLink: (state, action) => {
            state.links.splice(action.payload, 1);
        },
        changeTitle: (state, action) => {
            state.title = action.payload;
        },
        changeType: (state, action) => {
            state.type = action.payload;
        },
        changePageId: (state, action) => {
            state.pageId = action.payload;
        },
        changeIsMobileDisplayMode: (state, action) => {
            state.isMobileDisplayMode = action.payload;
        },
        changeIsMobileViewDimension: (state, action) => {
            state.isMobileViewDimension = action.payload;
        },
        changeIsExportState: (state, action) => {
            state.isExportState = action.payload;
        },
        changeIsPublishPageState: (state, action) => {
            state.isPublishPageState = action.payload;
        },
        setPublishEventContext: (state, action) => {
            state.publishEventContext = action.payload;
        },
        changeIsAdvancedMode: (state, action) => {
            state.isAdvancedMode = action.payload;
        },
        changeIsPremiumMeQr: (state, action) => {
            state.premiumMeQr = action.payload
        },
        changeIsReadOnlyMode: (state, action) => {
            state.isReadOnlyMode = action.payload;
        },
        changeScreenShotNodeId: (state, action) => {
            state.screenShotNodeId = action.payload;
        },
        toggleShowHiddenBlocks: (state) => {
            state.showHiddenBlocks = !state.showHiddenBlocks;
        },
        changeUser: (state, action) => {
            state.user = action.payload;
        },
        changeNewNode: (state, action) => {
            state.newNode = action.payload
        },
        setInitPublishPage: (state, action) => {
            state.initPublishPage = action.payload
        },
        changePublishStatus: (state, action) => {
            state.publishStatus = {isLoading: action.payload.isLoading, isCompleted: action.payload.isCompleted}
        },
        setCopiedContent: (state, action) => {
            state.isCopiedContent = action.payload
        },
        setIsClearElements: (state, action) => {
            state.isClearElements = action.payload
        },
        setIsRemovedBlock: (state, action) => {
            state.isRemovedBlock = action.payload
        },
        setIsShowPreloader: (state, action) => {
            state.isShowPreloader = action.payload
        },
        setIsInitApp: (state, action) => {
            state.isInitApp = action.payload
        },
        setIsCopiedBlock: (state, action) => {
            state.isCopiedBlock = action.payload
        },
        setIsNotShowPreviewToast: (state, action) => {
            state.isNotShowPreviewToast = action.payload
        },
        setSubPageLoading: (state, action) => {
            state.subPageLoading = action.payload
        },
        setIframeError(state, action) {
          state.iframeError = action.payload
        },
        setIsLoaded(state, action) {
          state.isLoaded = action.payload
        },
        setIsIframeOnMeQr(state, action) {
            state.isIframeOnMeQr = action.payload
        },
        changeFormPreview(state, action) {
            state.formPreview = action.payload
        },
        setNodesIdsWithWrongAction: (state, action) => {
            state.nodesIdsWithWrongAction = action.payload;
        },
        setPageWatermark: (state, action) => {
            state.pageWatermark = action.payload;
        },
        setIsAccountBlocked(state, action) {
          state.isAccountBlocked = action.payload;
        },
    },
});
export const {
    changePreview,
    addLink,
    removeLink,
    changePageId,
    changeTitle,
    changeType,
    changeIsMobileDisplayMode,
    changeIsExportState,
    changeIsPublishPageState,
    setPublishEventContext,
    changeIsMobileViewDimension,
    changeIsAdvancedMode,
    changeIsReadOnlyMode,
    toggleShowHiddenBlocks,
    changeUser,
    changeScreenShotNodeId,
    changeNewNode,
    setInitPublishPage,
    changePublishStatus,
    setCopiedContent,
    setIsClearElements,
    setIsRemovedBlock,
    setIsShowPreloader,
    setIsInitApp,
    changeIsPremiumMeQr,
    setIsCopiedBlock,
    setIsNotShowPreviewToast,
    setSubPageLoading,
    setIsLoaded,
    setIframeError,
    setIsIframeOnMeQr,
    changeFormPreview,
    setNodesIdsWithWrongAction,
    setPageWatermark,
    setIsAccountBlocked,
} = AppStorage.actions;
export default AppStorage.reducer;
